import { Injectable, signal, computed } from '@angular/core';
import { BloqueioRecurso, PlanInfo, PlanOption, PlanType } from '../models/plan.model';
import { PlanPayload } from '../models/auth.model';

const PLAN_LABELS: Record<string, string> = {
  trial: 'Trial',
  solo: 'Solo',
  consultoria: 'Consultoria',
  agencia: 'Agência',
  enterprise: 'Enterprise',
};

const TICK_MS = 60_000;
const DAY_MS = 86_400_000;

/**
 * PlanService — provides subscription plan data and limit-check helpers.
 * Plan state is hydrated from /api/auth/me payload via fromAuthMe().
 * `trialDaysRemaining` is computed live from `trialEndsAt` + a 60s tick,
 * so the UI updates without page reload.
 */
@Injectable({ providedIn: 'root' })
export class PlanService {
  private readonly _now = signal(Date.now());

  private readonly _plan = signal<PlanInfo>(this.emptyPlan());

  private readonly _planOptions = signal<PlanOption[]>([
    { type: 'trial',       label: 'Trial',       monthlyPrice: 0,      jobsTotal: 4,   talentsTotal: 1000,  aiCreditsTotal: 50 },
    { type: 'solo',        label: 'Solo',         monthlyPrice: 9900,   jobsTotal: 4,   talentsTotal: 1000,  aiCreditsTotal: 50 },
    { type: 'consultoria', label: 'Consultoria',  monthlyPrice: 24900,  jobsTotal: 12,  talentsTotal: 5000,  aiCreditsTotal: 200 },
    { type: 'agencia',     label: 'Agência',      monthlyPrice: 59900,  jobsTotal: 30,  talentsTotal: -1,    aiCreditsTotal: 600 },
    { type: 'enterprise',  label: 'Enterprise',   monthlyPrice: null,   jobsTotal: -1,  talentsTotal: -1,    aiCreditsTotal: -1 },
  ]);

  readonly plan = this._plan.asReadonly();
  readonly planOptions = this._planOptions.asReadonly();

  readonly isTrial = computed(() => this._plan().type === 'trial');

  readonly trialDaysRemaining = computed((): number | null => {
    const p = this._plan();
    if (p.type !== 'trial' || !p.trialEndsAt) return null;
    const diffMs = new Date(p.trialEndsAt).getTime() - this._now();
    return Math.max(0, Math.ceil(diffMs / DAY_MS));
  });

  readonly trialUrgency = computed((): 'info' | 'warning' | 'critical' | null => {
    const p = this._plan();
    if (p.type !== 'trial') return null;
    const days = this.trialDaysRemaining();
    if (days === null) return null;
    if (days === 0) return 'critical';
    if (days <= 3) return 'critical';
    if (days <= 7) return 'warning';
    return 'info';
  });

  constructor() {
    setInterval(() => this._now.set(Date.now()), TICK_MS);
  }

  // ── AI Credits ──────────────────────────────────────────────────────────────

  readonly aiCreditsPercent = computed(() => {
    const p = this._plan();
    if (p.aiCreditsTotal === -1) return 0;
    const total = p.aiCreditsTotal + p.aiCreditsExtra;
    if (total === 0) return 100;
    return Math.round((p.aiCreditsUsed / total) * 100);
  });

  readonly aiCreditsAvailable = computed(() => {
    const p = this._plan();
    if (p.aiCreditsTotal === -1) return -1;
    return p.aiCreditsTotal + p.aiCreditsExtra - p.aiCreditsUsed;
  });

  readonly isAiCreditsLow = computed(() => {
    const available = this.aiCreditsAvailable();
    if (available === -1) return false;
    return available <= 10;
  });

  // ── Jobs ────────────────────────────────────────────────────────────────────

  readonly jobsPercent = computed(() => {
    const p = this._plan();
    if (p.jobsTotal === -1) return 0;
    if (p.jobsTotal === 0) return 100;
    return Math.round((p.jobsUsed / p.jobsTotal) * 100);
  });

  readonly jobsAvailable = computed(() => {
    const p = this._plan();
    if (p.jobsTotal === -1) return -1;
    return p.jobsTotal - p.jobsUsed;
  });

  readonly isJobsAtLimit = computed(() => {
    const p = this._plan();
    if (p.jobsTotal === -1) return false;
    return p.jobsUsed >= p.jobsTotal;
  });

  // ── Talents ─────────────────────────────────────────────────────────────────

  readonly talentsPercent = computed(() => {
    const p = this._plan();
    if (p.talentsTotal === -1) return 0;
    if (p.talentsTotal === 0) return 100;
    return Math.round((p.talentsUsed / p.talentsTotal) * 100);
  });

  readonly talentsAvailable = computed(() => {
    const p = this._plan();
    if (p.talentsTotal === -1) return -1;
    return p.talentsTotal - p.talentsUsed;
  });

  readonly isTalentsAtLimit = computed(() => {
    const p = this._plan();
    if (p.talentsTotal === -1) return false;
    return p.talentsUsed >= p.talentsTotal;
  });

  // ── Bloqueio modal ───────────────────────────────────────────────────────────

  private readonly _bloqueioRecurso = signal<BloqueioRecurso | null>(null);
  readonly bloqueioRecurso = this._bloqueioRecurso.asReadonly();

  openBloqueioModal(recurso: BloqueioRecurso): void {
    this._bloqueioRecurso.set(recurso);
  }

  closeBloqueioModal(): void {
    this._bloqueioRecurso.set(null);
  }

  checkLimit(recurso: BloqueioRecurso): BloqueioRecurso | null {
    if (recurso === 'vagas' && this.isJobsAtLimit()) return 'vagas';
    if (recurso === 'talentos' && this.isTalentsAtLimit()) return 'talentos';
    return null;
  }

  readonly upgradablePlanOptions = computed(() => {
    const currentType = this._plan().type;
    const order: PlanType[] = ['trial', 'solo', 'consultoria', 'agencia', 'enterprise'];
    const currentIndex = order.indexOf(currentType);
    const effectiveIndex = currentType === 'trial' ? order.indexOf('solo') : currentIndex;
    return this._planOptions().filter((opt) => {
      const idx = order.indexOf(opt.type);
      return idx >= effectiveIndex;
    });
  });

  setPlan(plan: PlanInfo): void {
    this._plan.set(plan);
  }

  setPlanOptions(options: PlanOption[]): void {
    this._planOptions.set(options);
  }

  /**
   * Drops plan state to a "no subscription" sentinel.
   * - trialEndsAt = null → trialDaysRemaining = null → trialUrgency = null → banner hidden.
   * - Guard `(trialDaysRemaining() ?? 0) === 0` returns true → redirects protected routes to /billing/planos.
   * - Counters at 0/0 → isJobsAtLimit / isTalentsAtLimit true → blocks resource creation.
   */
  clearPlan(): void {
    this._plan.set({
      type: 'trial',
      label: 'Sem plano',
      status: 'trial',
      trialEndsAt: null,
      aiCreditsUsed: 0,
      aiCreditsTotal: 0,
      aiCreditsExtra: 0,
      jobsUsed: 0,
      jobsTotal: 0,
      talentsUsed: 0,
      talentsTotal: 0,
    });
  }

  /**
   * Hydrates plan state from the `/api/auth/me` payload.
   * Backend status (TRIAL/ATIVO/INADIMPLENTE/CANCELADO) maps to PlanInfo.status (trial/ativo/vencido).
   * planoAtual vem em UPPERCASE do DB (SOLO, CONSULTORIA, ...) — normalizamos para minusculas
   * pra casar com PlanType. label prioriza payload.planoNome (vindo de AssinaturaPlano.Nome),
   * caindo para o map estatico e depois para "Sem plano" como ultimo recurso.
   */
  fromAuthMe(payload: PlanPayload): void {
    const beStatus = payload.status?.toUpperCase();
    const status = beStatus === 'ATIVO' ? 'ativo' : beStatus === 'TRIAL' ? 'trial' : 'vencido';
    const rawCodigo = (payload.planoAtual ?? '').toLowerCase();
    const knownTypes: PlanType[] = ['trial', 'solo', 'consultoria', 'agencia', 'enterprise'];
    const normalizedType = knownTypes.includes(rawCodigo as PlanType) ? (rawCodigo as PlanType) : 'trial';
    const type: PlanType = status === 'trial' ? 'trial' : normalizedType;
    const backendLabel = payload.planoNome?.trim();
    const label = backendLabel && backendLabel.length > 0
      ? backendLabel
      : (PLAN_LABELS[type] ?? 'Sem plano');

    this._plan.set({
      type,
      label,
      status,
      trialEndsAt: payload.trialEndsAt,
      renewalDate: payload.renewalDate ?? undefined,
      monthlyPrice: payload.monthlyPrice ?? undefined,
      aiCreditsUsed: payload.aiCreditsUsed,
      aiCreditsTotal: payload.aiCreditsTotal,
      aiCreditsExtra: payload.aiCreditsExtra,
      jobsUsed: payload.jobsUsed,
      jobsTotal: payload.jobsTotal,
      talentsUsed: payload.talentsUsed,
      talentsTotal: payload.talentsTotal,
    });
  }

  private emptyPlan(): PlanInfo {
    return {
      type: 'trial',
      label: '',
      status: 'trial',
      trialEndsAt: null,
      aiCreditsUsed: 0,
      aiCreditsTotal: 0,
      aiCreditsExtra: 0,
      jobsUsed: 0,
      jobsTotal: 0,
      talentsUsed: 0,
      talentsTotal: 0,
    };
  }
}
